import React, { FC, Fragment, useState, useCallback } from 'react'
import { Grid, Typography, Divider } from '@mui/material'
import { DataLoad } from 'src/components/ui'
import { useConfigurationListRequest, useStagingDevicesRequest, useStagingGatewaysRequest } from 'src/hooks/api'
import { ConfigurationParameterTypeEnum } from 'src/api'
import { EditableConfigurationParam } from './editableConfigurationParam'
import { InlineConfigurationParam } from './inlineConfigurationParam'
import { EditableMobileSessionParam } from './editableMobileSessionParam'
import { StagingDevicesList } from './stagingDevicesList'
import { EditableStagingDevices } from './editableStagingDevices'
import { EditableStagingGateways } from './editableStagingGateways'
import { SwitchConfigurationParam } from './switchConfigurationParam'

export const ConfigurationList: FC = () => {
  const [editId, setEditId] = useState<number | null>(null)
  const [editStagingDevices, setEditStagingDevices] = useState<boolean>(false)
  const [editStagingLorawanSensors, setEditStagingLorawanSensors] = useState<boolean>(false)
  const [editStagingGateways, setEditStagingGateways] = useState<boolean>(false)
  const { data, isInitialLoading, isError, error, refetch } = useConfigurationListRequest({
    params: { limit: 50 }
  })
  const { data: stagingClimitSensors, refetch: refetchStagingClimitSensors } = useStagingDevicesRequest({ deviceType: 'jobsite_conditions_monitor' })
  const { data: stagingLorawanSensors, refetch: refetchStagingLorawanSensors } = useStagingDevicesRequest({ deviceType: 'lorawan_sensor' })
  const { data: stagingGateways, refetch: refetchStagingGateways } = useStagingGatewaysRequest()

  const privacyConfigurationList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.PrivacyPolicyLink || param.type === ConfigurationParameterTypeEnum.TermsOfUseLink) || []
  }, [data])()

  const supportConfigurationList = useCallback(() => {
    return (
      data?.filter(
        (param) =>
          param.type === ConfigurationParameterTypeEnum.FloorcloudEmail ||
          param.type === ConfigurationParameterTypeEnum.FloorcloudPhone ||
          param.type === ConfigurationParameterTypeEnum.FloorcloudHowToLink ||
          param.type === ConfigurationParameterTypeEnum.ProductRequestedEmail ||
          param.type === ConfigurationParameterTypeEnum.GangboxEmail ||
          param.type === ConfigurationParameterTypeEnum.PlatformFeaturesLink
      ) || []
    )
  }, [data])()

  const sessionConfigurationList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.MobileSessionLimit) || []
  }, [data])()

  const nwfaConfigurationList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.NwfaEmail || param.type === ConfigurationParameterTypeEnum.NwfaPhone) || []
  }, [data])()

  const rapidRhConfigurationList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.RapidRhTimeValidationThresholdHours) || []
  }, [data])()

  const documentConfigurationList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.DocumentUploadSizeLimit) || []
  }, [data])()

  const integrationsList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.DisplayProcoreIntegration) || []
  }, [data])()

  const chartConfigurationList = useCallback(() => {
    return data?.filter((param) => param.type === ConfigurationParameterTypeEnum.GatOrientationChangeThreshold) || []
  }, [data])()

  const devicesOutageThresholdConfiguration = useCallback(() => {
    return data?.find((param) => param.type === ConfigurationParameterTypeEnum.DevicesOutageThreshold) || null
  }, [data])()

  const saveChanges = () => {
    setEditId(null)
    refetch()
  }

  const saveStagingDevicesChanges = () => {
    setEditStagingDevices(false)
    refetchStagingClimitSensors()
  }

  const saveStagingLorawanSensorsChanges = () => {
    setEditStagingLorawanSensors(false)
    refetchStagingLorawanSensors()
  }

  const saveStagingGatewaysChanges = () => {
    setEditStagingGateways(false)
    refetchStagingGateways()
  }

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item>
        <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Privacy</Typography>
              </Grid>
              {privacyConfigurationList.map(({ id, name, value }, idx, list) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableConfigurationParam id={id} value={value || ''} configParamName={name} onChange={saveChanges} onCancel={() => setEditId(null)} />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Support</Typography>
              </Grid>
              {supportConfigurationList.map(({ id, name, value }, idx, list) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableConfigurationParam id={id} value={value || ''} configParamName={name} onChange={saveChanges} onCancel={() => setEditId(null)} />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Sessions</Typography>
              </Grid>
              {sessionConfigurationList.map(({ id, name, value }, idx, list) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableMobileSessionParam id={id} value={value || ''} configParamName={name} onChange={saveChanges} onCancel={() => setEditId(null)} />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">NWFA</Typography>
              </Grid>
              {nwfaConfigurationList.map(({ id, name, value, type }, idx, list) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableConfigurationParam
                        id={id}
                        value={value || ''}
                        configParamName={name}
                        onChange={saveChanges}
                        onCancel={() => setEditId(null)}
                        isRequiredField={type !== ConfigurationParameterTypeEnum.NwfaEmail}
                      />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">RapidRH</Typography>
              </Grid>
              {rapidRhConfigurationList.map(({ id, name, value, type }, idx, list) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableConfigurationParam id={id} value={value || ''} configParamName={name} onChange={saveChanges} onCancel={() => setEditId(null)} isRequiredField />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 4 }}>
              <Typography variant="h5">Hardware</Typography>
            </Grid>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Staging CLIMIT Sensors</Typography>
              </Grid>
              {editStagingDevices ? (
                <EditableStagingDevices
                  stagingDevices={stagingClimitSensors || []}
                  onCancel={() => setEditStagingDevices(false)}
                  onChange={saveStagingDevicesChanges}
                  deviceType="jobsite_conditions_monitor"
                />
              ) : (
                <StagingDevicesList stagingDevices={stagingClimitSensors || []} onEdit={() => setEditStagingDevices(true)} />
              )}
            </Grid>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Staging LoRaWan Sensors</Typography>
              </Grid>
              {editStagingLorawanSensors ? (
                <EditableStagingDevices
                  stagingDevices={stagingLorawanSensors || []}
                  onCancel={() => setEditStagingLorawanSensors(false)}
                  onChange={saveStagingLorawanSensorsChanges}
                  deviceType="lorawan_sensor"
                />
              ) : (
                <StagingDevicesList stagingDevices={stagingLorawanSensors || []} onEdit={() => setEditStagingLorawanSensors(true)} />
              )}
            </Grid>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Staging LoRaWan Gateways</Typography>
              </Grid>
              {editStagingGateways ? (
                <EditableStagingGateways stagingGateways={stagingGateways || []} onCancel={() => setEditStagingGateways(false)} onChange={saveStagingGatewaysChanges} />
              ) : (
                <StagingDevicesList stagingDevices={stagingGateways || []} onEdit={() => setEditStagingGateways(true)} />
              )}
            </Grid>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Devices Outage Threshold</Typography>
              </Grid>
              {devicesOutageThresholdConfiguration ? (
                <Fragment>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === devicesOutageThresholdConfiguration.id ? (
                      <EditableConfigurationParam
                        id={devicesOutageThresholdConfiguration.id}
                        value={devicesOutageThresholdConfiguration.value || ''}
                        configParamName={devicesOutageThresholdConfiguration.name}
                        onChange={saveChanges}
                        onCancel={() => setEditId(null)}
                      />
                    ) : (
                      <InlineConfigurationParam
                        id={devicesOutageThresholdConfiguration.id}
                        name={devicesOutageThresholdConfiguration.name || ''}
                        value={devicesOutageThresholdConfiguration.value || ''}
                        onEdit={setEditId}
                      />
                    )}
                  </Grid>
                </Fragment>
              ) : null}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Documents</Typography>
              </Grid>
              {documentConfigurationList.map(({ id, name, value }, idx, list) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableConfigurationParam id={id} value={value || ''} configParamName={name} onChange={saveChanges} onCancel={() => setEditId(null)} />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Integrations</Typography>
              </Grid>
              {integrationsList.map(({ id, name, value }, idx) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    <Grid item container alignItems="center">
                      <Grid item flexGrow={1}>
                        <Typography fontWeight={500}>{name}</Typography>
                      </Grid>
                      <Grid item>
                        <SwitchConfigurationParam id={id} name={name} isVisible={value === 'true'} onChange={saveChanges} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
          <Divider flexItem={true} sx={{ mb: 2 }} />
          <Grid container direction="column" spacing={2}>
            <Grid item sx={{ pb: 2, pt: 2 }}>
              <Grid item sx={{ pb: 1 }}>
                <Typography variant="h6">Charts</Typography>
              </Grid>
              {chartConfigurationList.map(({ id, name, value }, idx) => (
                <Fragment key={idx}>
                  <Grid item container spacing={1} alignItems="center" sx={{ pb: 2 }}>
                    {editId === id ? (
                      <EditableConfigurationParam id={id} value={value || ''} configParamName={name} onChange={saveChanges} onCancel={() => setEditId(null)} />
                    ) : (
                      <InlineConfigurationParam id={id} name={name || ''} value={value || ''} onEdit={setEditId} />
                    )}
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
        </DataLoad>
      </Grid>
    </Grid>
  )
}
